import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { showToast } from "./toastSlice";
import { toast } from "react-toastify";
import getAPIbaseURL from "../../utils/helpers";
import i18n from "../../i18n";


const API_URL = `${getAPIbaseURL()}/roles`;


export const fetchRoles = createAsyncThunk("roles/fetchRoles", async (_, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_URL}/`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data;
    } catch (error) {
        toast.error(i18n.t("slices.rolesSlice.fetchError"));
        return thunkAPI.rejectWithValue(error.response?.data || "An error occurred.");
    }
});


export const createRole = createAsyncThunk(
    "roles/createRole",
    async (roleData, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.post(`${API_URL}/`, roleData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            thunkAPI.dispatch(showToast({ message: "Role created successfully!", type: "success" }));
            return response.data;
        } catch (error) {
            thunkAPI.dispatch(
                showToast({
                    message: error.response?.data || "Failed to create role.",
                    type: "error",
                })
            );
            return thunkAPI.rejectWithValue(error.response?.data || "An error occurred.");
        }
    }
);


export const fetchRoleById = createAsyncThunk(
    "roles/fetchRoleById",
    async (roleId, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}/${roleId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || "Error fetching role");
        }
    }
);


export const archiveRole = createAsyncThunk(
    "roles/archiveRole",
    async ({ roleId, updates }, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.put(`${API_URL}/${roleId}`, updates, {
                headers: { Authorization: `Bearer ${token}` },
            });
            thunkAPI.dispatch(
                showToast({ message: "Role updated successfully!", type: "success" })
            );
            return response.data;
        } catch (error) {
            thunkAPI.dispatch(
                showToast({
                    message: error.response?.data || "Failed to update role.",
                    type: "error",
                })
            );
            return thunkAPI.rejectWithValue(error.response?.data || "An error occurred.");
        }
    }
);


export const restoreRole = createAsyncThunk(
    "roles/restoreRole",
    async ({ roleId, updates }, thunkAPI) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.put(`${API_URL}/${roleId}/restore`, updates, {
                headers: { Authorization: `Bearer ${token}` },
            });
            thunkAPI.dispatch(
                showToast({ message: "Role restored successfully!", type: "success" })
            );
            return response.data;
        } catch (error) {
            thunkAPI.dispatch(
                showToast({
                    message: error.response?.data || "Failed to restore role.",
                    type: "error",
                })
            );
            return thunkAPI.rejectWithValue(error.response?.data || "An error occurred.");
        }
    }
);



const rolesSlice = createSlice({
    name: "roles",
    initialState: {
        items: [],
        item: null,
        isLoading: false,
        error: null,
        message: null,
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearMessage: (state) => {
            state.message = null;
        },
    },
    extraReducers: (builder) => {
        builder

            .addCase(fetchRoles.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchRoles.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                state.items = action.payload;
            })
            .addCase(fetchRoles.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(createRole.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createRole.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                state.items.push(action.payload);
                state.item = action.payload;
                state.message = 'Role created successfully.'
            })
            .addCase(createRole.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(fetchRoleById.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchRoleById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                state.item = action.payload;
                state.message = 'Role created successfully.'
            })
            .addCase(fetchRoleById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(archiveRole.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(archiveRole.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                state.items.filter((role) => role.id !== action.payload);
                state.item = null;
                state.message = 'Role archived successfully.'
            })
            .addCase(archiveRole.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(restoreRole.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(restoreRole.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                state.items.push(action.payload);
                state.item = action.payload;
                state.message = 'Role restore successfully.'
            })
            .addCase(restoreRole.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

    },
});

export const { clearError, clearMessage } = rolesSlice.actions;
export default rolesSlice.reducer;